import type { Transport, Client, Interceptor } from '@connectrpc/connect';
import { createClient } from '@connectrpc/connect';
import { createConnectTransport, createGrpcWebTransport } from '@connectrpc/connect-web';
import { QueryService } from '@kentik/schema/gen/ts/kentik/query/v20251204alpha1/query_pb';
import { Health } from '@kentik/schema/gen/ts/grpc/health/v1/health_pb';
import { KmetaService } from '@kentik/schema/gen/ts/kentik/kmeta/v202607alpha1/kmeta_pb';
import { CorrelationService } from '@kentik/schema/gen/ts/kentik/alerting/correlation/v202609/correlation_pb';

let _grpcTransport: Transport;

// haproxyfe routes requests carrying this header to node-grpc-connect.
const BACKEND_ROUTING_HEADER = 'x-kt-backend';
const BACKEND_ROUTING_VALUE = 'grpc-connect';

interface ClientMap {
  Health: Client<typeof Health>;
  QueryService: Client<typeof QueryService>;
  KmetaService: Client<typeof KmetaService>;
  CorrelationService: Client<typeof CorrelationService>;
}

let _clients: ClientMap | null = null;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function initializeGrpcTransport($auth: any, type: 'json' | 'grpc-web' = 'json') {
  const interceptors: Interceptor[] = [
    (next) => (req) => {
      req.header.set('X-CSRF-Token', $auth.csrfToken);
      req.header.set(BACKEND_ROUTING_HEADER, BACKEND_ROUTING_VALUE);
      return next(req);
    }
  ];

  if (type === 'grpc-web') {
    _grpcTransport = createGrpcWebTransport({ baseUrl: '/', useBinaryFormat: false, interceptors });
  } else {
    _grpcTransport = createConnectTransport({ baseUrl: '/', interceptors });
  }

  _clients = {
    Health: createClient(Health, _grpcTransport),
    QueryService: createClient(QueryService, _grpcTransport),
    KmetaService: createClient(KmetaService, _grpcTransport),
    CorrelationService: createClient(CorrelationService, _grpcTransport)
  };
}

export function getClient<K extends keyof ClientMap>(name: K): ClientMap[K] {
  if (!_grpcTransport) {
    throw new Error('GRPC transport not initialized, call initializeGrpcTransport() first');
  }
  if (!_clients || !_clients[name]) {
    throw new Error(`Unable to find GRPC service client: ${name}`);
  }
  return _clients[name];
}
